import { API_URL } from '../../constants/endpoints';
import sessionFetch from '../../middleware/sessionFetch';
import { contactsFromCompanies } from '../../constants/whatsapp-contacts';
import { actions as mainActions } from '../Root/actions';
import { NAME, types } from './reducer';

const FINISHED = ['done', 'failed', 'cancelled', 'paused'];
const WATCH_MS = 3000;
const WATCH_MAX_ERRORS = 5;
// Рассылки, за которыми уже следим: повторный вызов для той же рассылки ничего не делает.
const watching = new Set();

// Период статистики: число дней (7, 30, 90) или диапазон дат { from, to } вида 2026-10-01.
const periodQuery = (period) =>
  typeof period === 'object' && period
    ? `from=${period.from}&to=${period.to}`
    : `days=${period}`;

// Тост по итогу рассылки: что ушло, сколько ошибок, остановлена ли.
const finishAlert = (name, data) => {
  const total = data.total || data.sent + data.failed + data.stopped;
  const counts = `Отправлено ${data.sent} из ${total}${
    data.failed > 0 ? ` · ошибок: ${data.failed}` : ''
  }${data.stopped > 0 ? ` · не отправлено: ${data.stopped}` : ''}`;
  const byStatus = {
    done: { title: 'Рассылка завершена', severity: data.failed > 0 ? 'warning' : 'success' },
    paused: { title: 'Рассылка остановлена', severity: 'warning' },
    cancelled: { title: 'Рассылка отменена', severity: 'warning' },
    failed: { title: 'Рассылка не отправлена', severity: 'error' },
  }[data.status];
  return {
    show: true,
    alertTitle: byStatus.title,
    alertText: `«${name}» · ${data.status === 'failed' ? data.error || 'неизвестная ошибка' : counts}`,
    severity: byStatus.severity,
    style: {},
    variant: 'outlined',
  };
};

export const actions = {
  // Следит за рассылкой, пока она не закончится, и показывает тост с итогом. Работает
  // независимо от окна отправки: его можно закрыть, а страницу сменить внутри приложения.
  watchBroadcast: (id, name) => async (dispatch) => {
    if (watching.has(id)) return;
    watching.add(id);
    let errors = 0;
    try {
      while (errors < WATCH_MAX_ERRORS) {
        await new Promise((resolve) => setTimeout(resolve, WATCH_MS));
        const result = await dispatch(actions.fetchBroadcast(id));
        if (!result.ok) {
          errors += 1;
        } else if (FINISHED.includes(result.data.status)) {
          dispatch(mainActions.showAlert(finishAlert(name, result.data)));
          return;
        } else {
          errors = 0;
        }
      }
    } finally {
      watching.delete(id);
    }
  },

  fetchBroadcastLists: () => async (dispatch) => {
    dispatch({ type: types.LISTS_LOADING });
    try {
      const res = await sessionFetch(`${API_URL}/api/broadcast-lists`);
      const json = await res.json();
      if (json.status === 'ok') {
        dispatch({ type: types.SET_LISTS, payload: json.data });
      } else {
        dispatch({ type: types.LISTS_ERROR, payload: 'Ошибка загрузки списков' });
      }
    } catch (err) {
      dispatch({ type: types.LISTS_ERROR, payload: err.message });
    }
  },

  saveBroadcastList: (list) => async (dispatch) => {
    try {
      const isNew = typeof list.id === 'string' || !list.id;
      const url = isNew
        ? `${API_URL}/api/broadcast-lists`
        : `${API_URL}/api/broadcast-lists/${list.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const res = await sessionFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: list.name, contactIds: list.contactIds }),
      });
      const json = await res.json();

      if (json.status === 'ok') {
        dispatch({ type: types.SAVE_LIST, payload: json.data });
        return json.data;
      }
      // Что ответил сервер (например, «Список с таким названием уже есть»), чтобы показать человеку.
      return { error: (json.data && json.data.type) || 'Не удалось сохранить список. Попробуйте ещё раз.' };
    } catch (err) {
      console.error('saveBroadcastList error', err);
    }
    return { error: 'Не удалось сохранить список: сервер недоступен. Попробуйте ещё раз.' };
  },

  deleteBroadcastList: (id) => async (dispatch) => {
    try {
      const res = await sessionFetch(`${API_URL}/api/broadcast-lists/${id}`, {
        method: 'DELETE',
      });
      const json = await res.json();
      if (json.status === 'ok') {
        dispatch({ type: types.DELETE_LIST, payload: id });
      }
    } catch (err) {
      console.error('deleteBroadcastList error', err);
    }
  },

  fetchTemplates: () => async (dispatch) => {
    dispatch({ type: types.TEMPLATES_LOADING });
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/templates`);
      const json = await res.json();
      if (json.status === 'ok') {
        dispatch({ type: types.SET_TEMPLATES, payload: json.data });
      } else {
        dispatch({
          type: types.TEMPLATES_ERROR,
          payload: (json.data && json.data.type) || 'Не удалось загрузить шаблоны',
        });
      }
    } catch (err) {
      dispatch({ type: types.TEMPLATES_ERROR, payload: err.message });
    }
  },

  // Возвращает { ok: true } или { ok: false, error } — текст ошибки от Meta.
  createTemplate: (template) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(template),
      });
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return {
        ok: false,
        error: (json.data && json.data.type) || 'Не удалось отправить шаблон',
      };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Шаблон со всем, что нужно форме редактирования: текст, подписи переменных,
  // картинка и раскладка текстов на ней. Возвращает { ok, data } или { ok: false, error }.
  fetchTemplate: (id) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/templates/${id}`);
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return {
        ok: false,
        error: (json.data && json.data.type) || 'Не удалось открыть шаблон',
      };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Отправляет изменения шаблона в Meta. Возвращает { ok: true } или { ok: false, error }.
  updateTemplate: (id, template) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/templates/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(template),
      });
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return {
        ok: false,
        error: (json.data && json.data.type) || 'Не удалось сохранить изменения',
      };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  fetchConversations: () => async (dispatch) => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/conversations`);
      const json = await res.json();
      if (json.status === 'ok') {
        dispatch({ type: types.SET_CONVERSATIONS, payload: json.data });
      } else {
        dispatch({
          type: types.CONVERSATIONS_ERROR,
          payload: (json.data && json.data.type) || 'Не удалось загрузить переписки',
        });
      }
    } catch (err) {
      dispatch({ type: types.CONVERSATIONS_ERROR, payload: err.message });
    }
  },

  fetchThread: (phone) => async (dispatch) => {
    try {
      const res = await sessionFetch(
        `${API_URL}/api/whatsapp/conversations/${phone}/messages`
      );
      const json = await res.json();
      if (json.status === 'ok') {
        dispatch({
          type: types.SET_THREAD,
          payload: { phone, messages: json.data },
        });
      }
    } catch (err) {
      console.error('fetchThread error', err);
    }
  },

  fetchReplyState: (phone) => async (dispatch) => {
    try {
      const res = await sessionFetch(
        `${API_URL}/api/whatsapp/conversations/${phone}/reply-state`
      );
      const json = await res.json();
      if (json.status === 'ok') {
        dispatch({
          type: types.SET_REPLY_STATE,
          payload: { ...json.data, phone, receivedAt: Date.now() },
        });
      }
    } catch (err) {
      console.error('fetchReplyState error', err);
    }
  },

  // Ответ клиенту обычным текстом. Все правила (окно 24 часа, лимиты) проверяет
  // сервер; экран лишь не даёт нажать кнопку, когда отправка заведомо невозможна.
  sendReply: (phone, text) => async (dispatch) => {
    try {
      const res = await sessionFetch(
        `${API_URL}/api/whatsapp/conversations/${phone}/reply`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        }
      );
      const json = await res.json();
      if (json.status === 'ok') {
        dispatch({
          type: types.SET_REPLY_STATE,
          payload: { ...json.data, phone, receivedAt: Date.now() },
        });
        return { ok: true };
      }
      dispatch(actions.fetchReplyState(phone));
      return {
        ok: false,
        error: (json.data && json.data.type) || 'Не удалось отправить сообщение',
      };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  markConversationSeen: (phone) => async (dispatch) => {
    try {
      await sessionFetch(`${API_URL}/api/whatsapp/conversations/${phone}/seen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      dispatch(actions.fetchConversations());
    } catch (err) {
      console.error('markConversationSeen error', err);
    }
  },

  // Удаляет шаблон в Meta. Возвращает { ok: true } или { ok: false, error }.
  deleteTemplate: (id) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/templates/${id}`, {
        method: 'DELETE',
      });
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return {
        ok: false,
        error: (json.data && json.data.type) || 'Не удалось удалить шаблон',
      };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Создаёт рассылку. Сервер отвечает сразу, а сообщения уходят в фоне (сейчас или в
  // выбранное время). Возвращает { ok, data } или { ok: false, error, code }.
  sendBroadcast: (payload) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return {
        ok: false,
        error: (json.data && json.data.type) || 'Не удалось создать рассылку',
        code: json.data && json.data.code,
      };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Что сделает отправка прямо сейчас, ничего не делая: сколько человек реально получат
  // сообщение, кто остаётся без него и почему, не ночь ли. Возвращает { ok, data }.
  previewBroadcast: (payload) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/send-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return { ok: false, error: (json.data && json.data.type) || 'Не удалось проверить отправку' };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // История рассылок (последние 100).
  fetchBroadcasts: () => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/broadcasts`);
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return { ok: false, error: (json.data && json.data.type) || 'Не удалось загрузить историю' };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Одна рассылка с получателями. status: pending | sent | failed | skipped | unknown.
  fetchBroadcast: (id, status) => async () => {
    try {
      const query = status ? `?status=${status}` : '';
      const res = await sessionFetch(`${API_URL}/api/whatsapp/broadcasts/${id}${query}`);
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return { ok: false, error: (json.data && json.data.type) || 'Не удалось загрузить рассылку' };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Останавливает рассылку: что уже отправлено, остаётся, остальное не уходит.
  // series: true — остановить и все ещё не завершённые рассылки той же серии.
  cancelBroadcast: (id, series = false) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/broadcasts/${id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ series }),
      });
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return { ok: false, error: (json.data && json.data.type) || 'Не удалось остановить рассылку' };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Останавливает рассылку на время: её можно запустить снова. Ожидающая не уйдёт в своё время,
  // идущая остановится перед следующим сообщением, остальным получателям ничего не пропадёт.
  // series: true — остановить и все активные рассылки той же серии.
  pauseBroadcast: (id, series = false) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/broadcasts/${id}/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ series }),
      });
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return { ok: false, error: (json.data && json.data.type) || 'Не удалось остановить рассылку' };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Запускает рассылку: ожидающую отправляет сразу, остановленную продолжает (или возвращает
  // в расписание, если её время ещё не пришло). series: true — запустить остановленные серии.
  // allowQuietHours: ночью сервер спрашивает подтверждение (код QUIET_HOURS).
  startBroadcast: (id, series = false, allowQuietHours = false) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/broadcasts/${id}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ series, ...(allowQuietHours ? { allowQuietHours: true } : {}) }),
      });
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return {
        ok: false,
        error: (json.data && json.data.type) || 'Не удалось запустить рассылку',
        code: json.data && json.data.code,
      };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Правит рассылку: название и/или время (sendAt или sendAtList + scheduleLabel). Время
  // меняется, пока рассылка не начала отправляться; получатели и шаблон остаются прежними.
  updateBroadcast: (id, changes) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/broadcasts/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changes),
      });
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return {
        ok: false,
        error: (json.data && json.data.type) || 'Не удалось сохранить изменения',
        code: json.data && json.data.code,
      };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Отправляет ещё раз тем, кому сообщение не ушло (ошибки, неизвестно, остановка, лимит).
  // Делается руками: само ничего повторно не отправляется.
  resendBroadcast: (id) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/broadcasts/${id}/resend`, { method: 'POST' });
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return { ok: false, error: (json.data && json.data.type) || 'Не удалось переотправить' };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Журнал рассылки: что не ушло и когда, переотправки. Сначала новые.
  fetchBroadcastLog: (id) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/broadcasts/${id}/log`);
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return { ok: false, error: (json.data && json.data.type) || 'Не удалось загрузить журнал' };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Удаляет рассылку из истории (сообщения в «Сообщениях» остаются). Идущую сначала надо остановить.
  // series: true — и остальные рассылки той же серии.
  deleteBroadcast: (id, series = false) => async () => {
    try {
      const res = await sessionFetch(
        `${API_URL}/api/whatsapp/broadcasts/${id}${series ? '?series=1' : ''}`,
        { method: 'DELETE' }
      );
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return { ok: false, error: (json.data && json.data.type) || 'Не удалось удалить рассылку' };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Состояние номера и аккаунта у Meta, лимит рассылок, вебхук и профиль компании.
  // Возвращает { ok, data } или { ok: false, error }.
  fetchAccount: (refresh = false) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/account${refresh ? '?refresh=1' : ''}`);
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return {
        ok: false,
        error: (json.data && json.data.type) || 'Не удалось загрузить данные аккаунта',
      };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Меняет профиль компании, который видят клиенты. Отправляются только изменённые поля.
  saveProfile: (fields) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/account/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return {
        ok: false,
        error: (json.data && json.data.type) || 'Не удалось сохранить профиль',
      };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Направляет вебхук Meta на другой адрес (новый туннель, новый сервер). Токен проверки
  // сервер берёт из своих настроек, на экран он не попадает.
  saveWebhook: (url) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/account/webhook`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return { ok: false, error: (json.data && json.data.type) || 'Не удалось сменить адрес вебхука' };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Возвращает адрес вебхука, заданный в приложении Meta (отменяет заданный отсюда).
  resetWebhook: () => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/account/webhook`, { method: 'DELETE' });
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return { ok: false, error: (json.data && json.data.type) || 'Не удалось вернуть адрес' };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Статистика рассылок за 7, 30 или 90 дней. Возвращает { ok, data } или { ok: false, error }.
  fetchStats: (period) => async () => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/stats?${periodQuery(period)}`);
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return {
        ok: false,
        error: (json.data && json.data.type) || 'Не удалось загрузить статистику',
      };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Расход и цена сообщения по данным Meta. days: 7, 30 или 90. Возвращает { ok, data } или
  // { ok: false, error }.
  fetchPricing: (period = 30, refresh = false) => async () => {
    try {
      const res = await sessionFetch(
        `${API_URL}/api/whatsapp/pricing?${periodQuery(period)}${refresh ? '&refresh=1' : ''}`
      );
      const json = await res.json();
      if (json.status === 'ok') {
        return { ok: true, data: json.data };
      }
      return { ok: false, error: (json.data && json.data.type) || 'Не удалось получить расходы' };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },

  // Клиенты: компании основной системы. Повторные вызовы в течение полминуты ничего не
  // делают (страницы просят их при открытии); force: true загружает заново.
  fetchCompanies: (force = false) => async (dispatch, getState) => {
    const { contactsLoadedAt } = getState()[NAME];
    if (!force && contactsLoadedAt && Date.now() - contactsLoadedAt < 30000) return;
    try {
      const res = await sessionFetch(`${API_URL}/api/companyList`);
      const json = await res.json();
      if (!res.ok || !Array.isArray(json)) {
        throw new Error((json && json.data && json.data.type) || `ответ ${res.status}`);
      }
      dispatch({ type: types.SET_COMPANIES, payload: contactsFromCompanies(json) });
    } catch (err) {
      console.error('fetchCompanies error', err);
      dispatch({ type: types.COMPANIES_ERROR, payload: err.message });
    }
  },

  // Что сервер знает о согласии: ответы клиентов на приветствие, СТОП, ручные отметки.
  fetchConsents: () => async (dispatch) => {
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/consents`);
      const json = await res.json();
      if (json.status === 'ok') {
        dispatch({ type: types.SET_CONSENTS, payload: json.data });
      }
    } catch (err) {
      console.error('fetchConsents error', err);
    }
  },

  // Ручная отметка менеджера. status: confirmed | declined | none (сбросить).
  // Возвращает { ok: true } или { ok: false, error }.
  markConsent: (contactIds, status, source, evidence) => async (dispatch, getState) => {
    const wanted = new Set(contactIds);
    const phones = getState()[NAME].contacts
      .filter((contact) => wanted.has(contact.id))
      .map((contact) => contact.phone.replace(/\D/g, ''));
    try {
      const res = await sessionFetch(`${API_URL}/api/whatsapp/consents/mark`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phones, status, source, evidence }),
      });
      const json = await res.json();
      if (json.status === 'ok') {
        await dispatch(actions.fetchConsents());
        // Сколько отказавших сервер не тронул: отказ снимает только сам клиент.
        return { ok: true, leftDeclined: (json.data && json.data.leftDeclined) || 0 };
      }
      return {
        ok: false,
        error: (json.data && json.data.type) || 'Не удалось сохранить отметку',
      };
    } catch (err) {
      return { ok: false, error: `Сервер недоступен: ${err.message}` };
    }
  },
};
